import { useQuery, type QueryKey } from '@tanstack/react-query';

interface ApiState<T> {
  data: T | undefined;
  error: Error | undefined;
  loading: boolean;
  reload: () => void;
}

interface ApiOptions<T> {
  /// False keeps the request from being made at all - for data the current user may not read,
  /// or a route parameter that is not there yet.
  enabled?: boolean;
  /// How long the answer counts as fresh. Past it, the next screen that asks refetches in the
  /// background while still showing what it has.
  staleTime?: number;
  /// Refetches on a timer. For screens that show something happening right now - and, given a
  /// function, only while what it last answered says something is still happening.
  refetchInterval?: number | ((data: T | undefined) => number | false);
}

/// Reads an endpoint. The key identifies the answer in the cache, so a screen that has been
/// visited before renders from what is already there and refetches behind the picture instead
/// of showing a spinner over data it could have shown.
///
/// The key is explicit rather than derived from the function, because two endpoints that take
/// no arguments would otherwise share one entry.
export function useApi<T>(
  key: QueryKey,
  load: () => Promise<T>,
  options: ApiOptions<T> = {},
): ApiState<T> {
  const { refetchInterval } = options;

  const query = useQuery({
    queryKey: key,
    queryFn: load,
    enabled: options.enabled ?? true,
    staleTime: options.staleTime,
    refetchInterval:
      typeof refetchInterval === 'function' ? (current) => refetchInterval(current.state.data) : refetchInterval,
  });

  return {
    data: query.data,
    error: (query.error as Error | null) ?? undefined,

    // Only while there is nothing to show. A refetch over data that is already on screen is
    // not a loading state - replacing a page with a spinner every few seconds is worse than
    // showing something a moment out of date.
    loading: query.isPending && query.fetchStatus !== 'idle',
    reload: () => void query.refetch(),
  };
}
